import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Eye, User, Tag, Share2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { API_URL, formatDate } from '../lib/utils';

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      const response = await fetch(`${API_URL}/blogs/${slug}`);
      if (response.ok) {
        const data = await response.json();
        setPost(data);
      }
    } catch (error) {
      console.error('Error fetching post:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#003366] border-t-transparent"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-3xl font-bold text-[#003366] mb-4">Article non trouvé</h1>
          <p className="text-gray-600 mb-8">L'article que vous recherchez n'existe pas ou a été supprimé.</p>
          <Link to="/blog">
            <Button className="bg-[#003366] hover:bg-[#002244] text-white rounded-full">
              <ArrowLeft size={18} className="mr-2" /> Retour au blog
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="blog-post-page">
      {/* Hero */}
      <section className="relative h-[50vh] min-h-[400px] bg-[#003366]">
        {post.cover_image && (
          <div className="absolute inset-0">
            <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover opacity-30" />
          </div>
        )}
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <span className="badge bg-[#DC143C] text-white mb-4">{post.category}</span>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-white mb-6">
                {post.title}
              </h1>
              <div className="flex items-center justify-center gap-6 text-white/80">
                <span className="flex items-center gap-2">
                  <User size={18} /> {post.author}
                </span>
                <span className="flex items-center gap-2">
                  <Calendar size={18} /> {formatDate(post.created_at)}
                </span>
                <span className="flex items-center gap-2">
                  <Eye size={18} /> {post.views} vues
                </span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4">
          <Link to="/blog" className="inline-flex items-center gap-2 text-[#003366] hover:text-[#DC143C] mb-8 font-medium">
            <ArrowLeft size={18} /> Retour au blog
          </Link>

          <motion.article
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="prose prose-lg max-w-none"
          >
            <p className="text-xl text-gray-600 leading-relaxed mb-8 font-medium">
              {post.excerpt}
            </p>
            
            <div 
              className="rich-content"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />
          </motion.article>

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="mt-12 pt-8 border-t">
              <div className="flex items-center gap-3 flex-wrap">
                <Tag size={18} className="text-gray-400" />
                {post.tags.map((tag, i) => (
                  <span key={i} className="bg-gray-100 px-3 py-1 rounded-full text-sm text-gray-600">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Share */}
          <div className="mt-8 flex items-center gap-4">
            <span className="text-gray-600 font-medium">Partager :</span>
            <Button variant="outline" size="sm" className="rounded-full">
              <Share2 size={16} className="mr-2" /> Partager
            </Button>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading text-2xl font-bold text-[#003366] mb-4">
            Intéressé par nos formations ?
          </h2>
          <p className="text-gray-600 mb-8">
            Découvrez nos programmes et lancez votre carrière avec CEFAT Business School.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to="/preinscription">
              <Button className="bg-[#DC143C] hover:bg-[#B01030] text-white rounded-full px-8">
                Je me préinscris
              </Button>
            </Link>
            <Link to="/blog">
              <Button variant="outline" className="border-[#003366] text-[#003366] rounded-full px-8">
                Voir plus d'articles
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
